import React, { useState } from 'react';
import { IconButton } from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { changePassword } from '../apis/userAPI';
import './ChangePasswordModal.css';

const EMPTY_FORM = { currentPassword: '', newPassword: '', confirmPassword: '' };

// Modal for changing a user's password. targetEmail is the account whose password is being
// changed; currentPassword always authenticates the person submitting the form (self or an
// admin acting on another user's behalf).
const ChangePasswordModal = ({ open, targetEmail, onClose, onSuccess }) => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [visibility, setVisibility] = useState({ current: false, new: false, confirm: false });
  const [confirmError, setConfirmError] = useState('');
  const [formError, setFormError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!open) return null;

  const resetAndClose = () => {
    setForm(EMPTY_FORM);
    setVisibility({ current: false, new: false, confirm: false });
    setConfirmError('');
    setFormError('');
    setIsSubmitting(false);
    onClose();
  };

  const handleFieldChange = (field, value) => {
    const nextForm = { ...form, [field]: value };
    setForm(nextForm);
    if (field === 'newPassword' || field === 'confirmPassword') {
      setConfirmError(
        nextForm.confirmPassword && nextForm.newPassword !== nextForm.confirmPassword
          ? 'Passwords do not match'
          : ''
      );
    }
    setFormError('');
  };

  const toggleVisibility = (field) => {
    setVisibility((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.newPassword !== form.confirmPassword) {
      setConfirmError('Passwords do not match');
      return;
    }
    setIsSubmitting(true);
    try {
      await changePassword({
        email: targetEmail,
        currentPassword: form.currentPassword,
        newPassword: form.newPassword,
      });
      resetAndClose();
      if (onSuccess) onSuccess();
    } catch (error) {
      setFormError(error.message || 'Failed to change password');
      setIsSubmitting(false);
    }
  };

  const fields = [
    { key: 'current', name: 'currentPassword', label: 'Current Password' },
    { key: 'new', name: 'newPassword', label: 'New Password' },
    { key: 'confirm', name: 'confirmPassword', label: 'Confirm New Password' },
  ];

  return (
    <div className="cp-modal-overlay" role="presentation">
      <div
        className="cp-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="change-password-modal-title"
      >
        <div className="cp-modal-header">
          <h3 id="change-password-modal-title" className="cp-modal-title">Change Password</h3>
        </div>

        <form onSubmit={handleSubmit} className="cp-modal-form">
          {fields.map(({ key, name, label }) => (
            <label className="cp-form-field" key={name}>
              <span>{label}</span>
              <div className="cp-password-input-container">
                <input
                  type={visibility[key] ? 'text' : 'password'}
                  name={name}
                  value={form[name]}
                  onChange={(e) => handleFieldChange(name, e.target.value)}
                  className="cp-password-input"
                  autoComplete="off"
                  required
                />
                <IconButton
                  type="button"
                  aria-label={visibility[key] ? 'Hide password' : 'Show password'}
                  onClick={() => toggleVisibility(key)}
                  className="cp-password-toggle-button"
                  size="small"
                  tabIndex={-1}
                >
                  {visibility[key] ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                </IconButton>
              </div>
              {name === 'confirmPassword' && confirmError && (
                <span className="cp-field-warning">{confirmError}</span>
              )}
            </label>
          ))}

          {formError && <p className="cp-form-error">{formError}</p>}

          <div className="cp-modal-actions">
            <button
              type="button"
              className="cp-btn cp-btn-secondary"
              disabled={isSubmitting}
              onClick={resetAndClose}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="cp-btn cp-btn-primary"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Submitting...' : 'Submit'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ChangePasswordModal;
